import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTENT_RATINGS, type ContentRating, formatPriceCents } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { X } from '@/components/icons';
import { adminApi } from './api';

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide',
        active ? 'bg-success/15 text-success' : 'bg-surface-raised text-ink-faint',
      )}
    >
      {active ? 'Live' : 'Hidden'}
    </span>
  );
}

export function GamesAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-games'], queryFn: adminApi.games });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-games'] });
  const [title, setTitle] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [rating, setRating] = useState<ContentRating | ''>('');

  const create = useMutation({
    mutationFn: () =>
      adminApi.createGame({ title, embedUrl: embedUrl || null, rating: rating || null, active: false }),
    onSuccess: () => {
      setTitle('');
      setEmbedUrl('');
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (g: { id: string; active: boolean }) => adminApi.updateGame(g.id, { active: !g.active }),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => adminApi.deleteGame(id), onSuccess: invalidate });

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-end gap-2 p-4">
        <Field label="Game title" value={title} onChange={(e) => setTitle(e.target.value)} className="min-w-[180px] flex-1" />
        <Input placeholder="Embed URL (optional)" value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} className="min-w-[180px] flex-1" />
        <select value={rating} onChange={(e) => setRating(e.target.value as ContentRating | '')} className="h-11 rounded-xl border border-border bg-surface-raised px-2 text-sm text-ink">
          <option value="">Rating</option>
          {CONTENT_RATINGS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <Button onClick={() => create.mutate()} disabled={!title || create.isPending}>Add game</Button>
      </div>
      <ul className="space-y-2">
        {(data?.games ?? []).map((g) => (
          <li key={g.id} className="card flex items-center gap-3 p-4">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">{g.title}</span>
              <span className="text-xs text-ink-faint">{g.rating ?? 'unrated'}</span>
            </span>
            <ActiveBadge active={g.active} />
            <Button size="sm" variant="secondary" onClick={() => toggle.mutate(g)}>
              {g.active ? 'Hide' : 'Publish'}
            </Button>
            <button type="button" onClick={() => del.mutate(g.id)} className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:text-danger" aria-label="Delete">
              <X size={15} />
            </button>
          </li>
        ))}
        {data && data.games.length === 0 && <li className="text-sm text-ink-muted">No games yet.</li>}
      </ul>
    </div>
  );
}

export function ProductsAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-products'], queryFn: adminApi.products });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-products'] });
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');

  const create = useMutation({
    mutationFn: () =>
      adminApi.createProduct({ name, priceCents: Math.round(Number(price) * 100) || 0, active: false }),
    onSuccess: () => {
      setName('');
      setPrice('');
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (p: { id: string; active: boolean }) => adminApi.updateProduct(p.id, { active: !p.active }),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => adminApi.deleteProduct(id), onSuccess: invalidate });

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-end gap-2 p-4">
        <Field label="Product name" value={name} onChange={(e) => setName(e.target.value)} className="min-w-[180px] flex-1" />
        <label className="text-sm text-ink-muted">
          Price $
          <input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="ml-1 h-11 w-24 rounded-xl border border-border bg-surface-raised px-2 text-sm text-ink" />
        </label>
        <Button onClick={() => create.mutate()} disabled={!name || !price || create.isPending}>Add product</Button>
      </div>
      <ul className="space-y-2">
        {(data?.products ?? []).map((p) => (
          <li key={p.id} className="card flex items-center gap-3 p-4">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">{p.name}</span>
              <span className="text-xs text-brand-soft">{formatPriceCents(p.priceCents)}</span>
            </span>
            <ActiveBadge active={p.active} />
            <Button size="sm" variant="secondary" onClick={() => toggle.mutate(p)}>
              {p.active ? 'Hide' : 'Publish'}
            </Button>
            <button type="button" onClick={() => del.mutate(p.id)} className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:text-danger" aria-label="Delete">
              <X size={15} />
            </button>
          </li>
        ))}
        {data && data.products.length === 0 && <li className="text-sm text-ink-muted">No products yet.</li>}
      </ul>
    </div>
  );
}
